import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import {
  createImageAnnotator,
  ImageAnnotation,
  RectangleGeometry,
  ShapeType,
} from '@annotorious/annotorious';
import '@annotorious/react/annotorious-react.css';

import {
  BBOX_COLORS,
  BBoxLayer,
  BBoxRect,
  NON_INTERSECTING_LAYERS,
} from '../../../Interfaces/Config/BboxConfigSetting';
import { EditConfigSetting } from '../../../Interfaces/Config/ConfigSetting';
import {
  isBBoxRectArray,
  isBBoxRectObject,
  isFiniteNumber,
} from '../../../utils/utils';
import { createStyles, makeStyles } from '@material-ui/core';
import { GetString } from '../../StringResourceManager';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';

const useStyles = makeStyles(() =>
  createStyles({
    rootGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 300px',
      gap: 16,
      minHeight: 620,
    },
    imageContainer: {
      border: '1px solid #d0d7de',
      borderRadius: 8,
      overflow: 'hidden',
      background: '#fff',
      minHeight: 600,
      padding: 8,
    },
    image: {
      maxWidth: '100%',
      display: 'block',
    },
    sidebar: {
      border: '1px solid #d0d7de',
      borderRadius: 8,
      padding: 12,
      background: '#fafafa',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    sidebarTitle: {
      fontWeight: 600,
    },
    sidebarDescription: {
      fontSize: 12,
      color: '#64748b',
      lineHeight: 1.5,
      marginBottom: 8,
    },
    layerItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 10px',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      background: '#fff',
      marginBottom: 4,
      cursor: 'pointer',
    },
    layerItemActive: {
      border: '2px solid',
      background: '#eff6ff',
    },
    colorDot: {
      width: 14,
      height: 14,
      borderRadius: '50%',
      flexShrink: 0,
    },
    layerLabel: {
      flex: 1,
      fontWeight: 600,
    },
    deleteContainer: {
      marginTop: 8,
    },
  }),
);

interface Props {
  open: boolean;
  imageUrl: string;
  bboxRows: EditConfigSetting[];
  onClose: () => void;
  onSave: (layers: BBoxLayer[]) => void;
}

type AnnotoriousAnnotation = ImageAnnotation & {
  properties?: {
    rowId?: number;
    label?: string;
    color?: string;
  };
};

const normalizeBBoxRect = (value: unknown): BBoxRect | null => {
  if (!isBBoxRectObject(value)) return null;

  const v = value as Record<string, unknown>;
  return {
    h: (v.h ?? v.height) as number,
    w: (v.w ?? v.width) as number,
    x: v.x as number,
    y: v.y as number,
  };
};

const parseBBoxRects = (raw: string): BBoxRect[] => {
  if (!raw) return [];

  try {
    const parsed: unknown = JSON.parse(raw);
    if (isBBoxRectObject(parsed)) {
      const rect = normalizeBBoxRect(parsed);
      return rect ? [rect] : [];
    }

    if (isBBoxRectArray(parsed)) {
      return (parsed as unknown[])
        .map((r: unknown) => normalizeBBoxRect(r))
        .filter((r): r is BBoxRect => r !== null);
    }

    if (parsed && typeof parsed === 'object') {
      const rects: BBoxRect[] = [];
      Object.values(parsed as Record<string, unknown>).forEach((v: unknown) => {
        if (isBBoxRectObject(v)) {
          const rect = normalizeBBoxRect(v);
          if (rect) rects.push(rect);
        } else if (isBBoxRectArray(v)) {
          rects.push(
            ...(v as unknown[])
              .map((r: unknown) => normalizeBBoxRect(r))
              .filter((r): r is BBoxRect => r !== null),
          );
        }
      });
      return rects;
    }
    return [];
  } catch {
    return [];
  }
};

const buildInitialAnnotations = (
  bboxRows: EditConfigSetting[],
): AnnotoriousAnnotation[] => {
  return bboxRows.flatMap((row, index) => {
    const effectiveValue =
      row.NewValue && row.NewValue.trim() !== ''
        ? row.NewValue
        : row.OriginalValue;

    const rects = parseBBoxRects(effectiveValue);
    const color = BBOX_COLORS[index % BBOX_COLORS.length];
    const label = row.Setting.Name;

    return rects.map((rect, rectIndex): AnnotoriousAnnotation => {
      const geometry: RectangleGeometry = {
        h: rect.h,
        w: rect.w,
        x: rect.x,
        y: rect.y,
        bounds: {
          minX: rect.x,
          minY: rect.y,
          maxX: rect.x + rect.w,
          maxY: rect.y + rect.h,
        },
      };

      return {
        id: `${row.ID}-${rectIndex}`,
        target: {
          selector: {
            type: ShapeType.RECTANGLE,
            geometry,
          },
          annotation: '',
        },
        bodies: [
          {
            purpose: 'tagging',
            value: label,
            id: '',
            annotation: '',
          },
        ],
        properties: {
          rowId: row.ID,
          label,
          color,
        },
      };
    });
  });
};

const groupAnnotationsBackToLayers = (
  annotations: AnnotoriousAnnotation[],
  bboxRows: EditConfigSetting[],
): BBoxLayer[] => {
  return bboxRows.map(row => {
    const matching = annotations.filter(
      annotation => annotation.properties?.rowId === row.ID,
    );

    const rects: BBoxRect[] = matching
      .map(annotation => annotation.target?.selector?.geometry)
      .filter((g): g is RectangleGeometry => !!g)
      .filter(
        g =>
          isFiniteNumber(g.x) &&
          isFiniteNumber(g.y) &&
          isFiniteNumber(g.w) &&
          isFiniteNumber(g.h),
      )
      .map(g => ({
        h: Math.round(g.h),
        w: Math.round(g.w),
        x: Math.round(g.x),
        y: Math.round(g.y),
      }));

    return {
      rowId: row.ID,
      label: row.Setting.Name,
      rects,
    };
  });
};

interface InnerProps {
  bboxRows: EditConfigSetting[];
  imageUrl: string;
  onCancel: () => void;
  onConfirm: (layers: BBoxLayer[]) => void;
}

const BBoxAnnotatorInner: React.FC<InnerProps> = ({
  bboxRows,
  imageUrl,
  onCancel,
  onConfirm,
}) => {
  const classes = useStyles();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const annoRef = useRef<any>(null);

  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<
    string | null
  >(null);
  const [liveAnnotations, setLiveAnnotations] = useState<
    AnnotoriousAnnotation[]
  >([]);
  const [initialAnnotations, setInitialAnnotations] = useState<
    AnnotoriousAnnotation[]
  >([]);
  const [colorMap, setColorMap] = useState<Record<number, string>>({});

  const activeRowIdRef = React.useRef<number | null>(null);

  const BOUNDING_BOX_OVERLAPS = GetString('BOUNDING_BOX_OVERLAPS');

  useEffect(() => {
    activeRowIdRef.current = activeRowId;
  }, [activeRowId]);

  useEffect(() => {
    const annotations = buildInitialAnnotations(bboxRows);
    setInitialAnnotations(annotations);
  }, [imageUrl]);

  useEffect(() => {
    if (bboxRows.length > 0 && activeRowId == null) {
      setActiveRowId(bboxRows[0].ID);
    }
  }, [bboxRows, activeRowId]);

  useEffect(() => {
    const map: Record<number, string> = {};
    bboxRows.forEach((row, index) => {
      map[row.ID] = BBOX_COLORS[index % BBOX_COLORS.length];
    });
    setColorMap(map);
  }, [bboxRows]);

  const getRowColor = (rowId: number): string => {
    return colorMap[rowId] ?? '#2563EB';
  };

  const getRowLabel = (rowId: number): string => {
    const row = bboxRows.find(r => r.ID === rowId);
    return row?.Setting?.Name ?? '';
  };

  const syncFromAnnotator = () => {
    const anno = annoRef.current;
    if (!anno || typeof anno.getAnnotations !== 'function') return;

    const result = anno.getAnnotations();
    const next = Array.isArray(result)
      ? (result as AnnotoriousAnnotation[])
      : [];
    setLiveAnnotations(next);
  };

  useEffect(() => {
    if (!imgRef.current) return;

    const anno = createImageAnnotator(imgRef.current, {
      style: (annotation: ImageAnnotation) => {
        const color = (annotation as any).properties?.color ?? '#2563EB';
        return {
          stroke: color,
          fill: color,
          fillOpacity: 0.08,
          strokeWidth: 3,
        };
      },
    });

    annoRef.current = anno;

    const handleCreate = (annotation: ImageAnnotation) => {
      const anno = annoRef.current;
      if (!anno) return;

      const rowId = activeRowIdRef.current;
      if (rowId === null) {
        if (annotation.id) {
          anno.removeAnnotation(annotation.id);
        }
        return;
      }

      const existing = anno
        .getAnnotations()
        .some((a: any) => a.properties?.rowId === rowId);

      if (existing) {
        anno.removeAnnotation(annotation.id);
        return;
      }

      const patched: AnnotoriousAnnotation = {
        ...annotation,
        properties: {
          ...(annotation as AnnotoriousAnnotation).properties,
          rowId,
          label: getRowLabel(rowId),
          color: getRowColor(rowId),
        },
      };

      anno.updateAnnotation(patched);
      syncFromAnnotator();
    };

    const handleUpdate = () => {
      syncFromAnnotator();
    };

    const handleDelete = () => {
      syncFromAnnotator();
      setSelectedAnnotationId(null);
    };

    const handleSelectionChanged = (selected: ImageAnnotation[]) => {
      const selectedAnno = selected?.[0];
      setSelectedAnnotationId(selected?.[0]?.id ?? null);

      const rowId = (selectedAnno as any)?.properties?.rowId;

      if (rowId != null) {
        setActiveRowId(rowId);
      }
    };

    anno.on?.('createAnnotation', handleCreate);
    anno.on?.('updateAnnotation', handleUpdate);
    anno.on?.('deleteAnnotation', handleDelete);
    anno.on?.('selectionChanged', handleSelectionChanged);
    anno.setAnnotations(initialAnnotations);
    syncFromAnnotator();

    return () => {
      anno.off?.('createAnnotation', handleCreate);
      anno.off?.('updateAnnotation', handleUpdate);
      anno.off?.('deleteAnnotation', handleDelete);
      anno.off?.('selectionChanged', handleSelectionChanged);
      anno.destroy?.();
      annoRef.current = null;
    };
  }, [imageUrl]);

  useEffect(() => {
    const anno = annoRef.current;
    if (!anno) return;
    anno.setAnnotations(initialAnnotations as any, true);
    setSelectedAnnotationId(null);
    syncFromAnnotator();
  }, [initialAnnotations]);

  const handleDeleteSelected = () => {
    const anno = annoRef.current;
    if (!anno) return;

    const selectedId =
      selectedAnnotationId ?? anno.getSelected?.()?.[0]?.id ?? null;

    if (!selectedId) return;

    anno.removeAnnotation(selectedId);
    setSelectedAnnotationId(null);
    syncFromAnnotator();
  };

  const areBoxesOverlapping = (a: BBoxRect, b: BBoxRect): boolean => {
    return !(
      a.x + a.w <= b.x ||
      b.x + b.w <= a.x ||
      a.y + a.h <= b.y ||
      b.y + b.h <= a.y
    );
  };

  const validateBoxes = (layers: BBoxLayer[]): string[] => {
    const errors: string[] = [];
    const relevantLayers = layers.filter(layer =>
      NON_INTERSECTING_LAYERS.has(layer.label.toLowerCase()),
    );

    for (let i = 0; i < relevantLayers.length; i++) {
      for (let j = i + 1; j < relevantLayers.length; j++) {
        const layerA = relevantLayers[i];
        const layerB = relevantLayers[j];

        const rectA = layerA.rects?.[0];
        const rectB = layerB.rects?.[0];

        if (!rectA || !rectB) continue;

        if (areBoxesOverlapping(rectA, rectB)) {
          errors.push(
            `${layerA.label} ${BOUNDING_BOX_OVERLAPS} ${layerB.label}`,
          );
        }
      }
    }
    return errors;
  };

  const handleSave = () => {
    const anno = annoRef.current;
    const current = anno?.getAnnotations?.() ?? [];
    const layers = groupAnnotationsBackToLayers(
      current as AnnotoriousAnnotation[],
      bboxRows,
    );

    const errors = validateBoxes(layers);

    if (errors.length > 0) {
      setValidationMessages(errors);
      return;
    }

    if (anno) {
      anno.setSelected?.();
      anno.cancelSelected?.();
    }
    onConfirm(layers);
  };

  useEffect(() => {
    if (validationMessages.length > 0) {
      const timer = setTimeout(() => setValidationMessages([]), 3000);
      return () => clearTimeout(timer);
    }
  }, [validationMessages]);

  return (
    <>
      <NotificationGroup
        style={{
          right: 0,
          bottom: 0,
          position: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        {validationMessages.map((msg, index) => (
          <Notification
            key={index}
            type={{ style: 'error', icon: true }}
            closable
            onClose={() =>
              setValidationMessages(prev => prev.filter((_, i) => i !== index))
            }
          >
            <span>{msg}</span>
          </Notification>
        ))}
      </NotificationGroup>
      <div className={classes.rootGrid}>
        <div className={classes.imageContainer}>
          <img ref={imgRef} src={imageUrl} className={classes.image} />
        </div>

        <div className={classes.sidebar}>
          <div className={classes.sidebarTitle}>
            {GetString('BOUNDING_BOX_AREAS')}
          </div>

          <div className={classes.sidebarDescription}>
            {GetString('BOUNDING_BOX_AREA_DESC')}
          </div>

          {bboxRows.map((row, index) => {
            const color = BBOX_COLORS[index % BBOX_COLORS.length];
            const label = row?.Setting?.Name;
            const isActive = activeRowId === row.ID;

            return (
              <div
                key={row.ID}
                onClick={() => {
                  setActiveRowId(row.ID);
                  const anno = annoRef.current;
                  if (anno && typeof anno.getAnnotations === 'function') {
                    const current = anno.getAnnotations();
                    anno.clearAnnotations();
                    current.forEach((a: ImageAnnotation) =>
                      anno.addAnnotation(a),
                    );
                  }
                }}
                className={`${classes.layerItem} ${
                  isActive ? classes.layerItemActive : ''
                }`}
                style={{
                  ...(isActive ? { borderColor: color } : {}),
                }}
              >
                <div
                  className={classes.colorDot}
                  style={{ background: color }}
                />

                <div style={{ flex: 1 }}>
                  <div className={classes.layerLabel}>{label}</div>
                </div>
              </div>
            );
          })}

          <div className={classes.deleteContainer}>
            <Button
              disabled={!selectedAnnotationId}
              onClick={handleDeleteSelected}
            >
              {GetString('DELETE_SELECTED_BOX')}
            </Button>
          </div>
        </div>
      </div>

      <DialogActionsBar>
        <Button onClick={onCancel}>{GetString('CANCEL_BUTTON')}</Button>
        <Button themeColor="primary" onClick={handleSave}>
          {GetString('SAVE_BUTTON')}
        </Button>
      </DialogActionsBar>
    </>
  );
};

const BboxAnnotatorDialog: React.FC<Props> = ({
  open,
  imageUrl,
  bboxRows,
  onClose,
  onSave,
}) => {
  if (!open) return null;

  return (
    <Dialog
      title={GetString('EDIT_BOUNDING_BOXES')}
      onClose={onClose}
      width={1280}
      height={800}
    >
      <BBoxAnnotatorInner
        bboxRows={bboxRows}
        imageUrl={imageUrl}
        onCancel={onClose}
        onConfirm={onSave}
      />
    </Dialog>
  );
};

export default BboxAnnotatorDialog;
